import { createStore, type Effect, type Event } from 'effector';
import storage from 'local-storage-fallback';

import { applyReducers } from '@m/effector-utils';
import { PORTAL_LAST_ENTID_STORAGE_KEY } from 'apps/portal/constants';

import { getAvailableEnterprises, setSelectedEntepriseId, updateAutoPaySetting } from './actions';

import type { DoneHandler } from '@m/effector-utils';
import type { Enterprise } from '@m/types/api';
import type { APIResponse } from '@m/utils/http';

const lastSelectedEnterpriseId = storage.getItem(PORTAL_LAST_ENTID_STORAGE_KEY);

export type EnterpriseState = {
  enterpriseList: Enterprise[];
  enterpriseMap: { [key: number]: Enterprise };
  selectedEnterpriseId: number | null;
};

const initialState: EnterpriseState = {
  enterpriseList: [],
  enterpriseMap: {},
  selectedEnterpriseId: lastSelectedEnterpriseId !== null ? Number(lastSelectedEnterpriseId) : null,
};

const store = createStore(initialState);

type Reducers = {
  getAvailableEnterprises: {
    action: Effect<Parameters<typeof getAvailableEnterprises>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof getAvailableEnterprises>[0], EnterpriseState>;
  };
  setSelectedEntepriseId: {
    action: Event<number | null>;
    reducer: (state: EnterpriseState, payload: number | null) => EnterpriseState;
  };
  updateAutoPaySetting: {
    action: Event<{ enabled: boolean; enterpriseId: number }>;
    reducer: (
      state: EnterpriseState,
      payload: { enabled: boolean; enterpriseId: number },
    ) => EnterpriseState;
  };
};

function createEnterpriseMap(enterprises: Enterprise[]) {
  return enterprises.reduce(
    (coll, enterprise) => ({
      ...coll,
      [enterprise.id]: enterprise,
    }),
    {},
  );
}

export const reducers: Reducers = {
  getAvailableEnterprises: {
    action: getAvailableEnterprises,
    done: (state, { result: { success, data } = {} }) => ({
      ...state,
      enterpriseList: success ? data.enterprises : state.enterpriseList,
      enterpriseMap: success ? createEnterpriseMap(data.enterprises) : state.enterpriseMap,
      selectedEnterpriseId: state.selectedEnterpriseId ?? data.enterprises[0]?.id ?? null,
    }),
  },
  setSelectedEntepriseId: {
    action: setSelectedEntepriseId,
    reducer: (state, selectedEnterpriseId) => {
      if (selectedEnterpriseId !== state.selectedEnterpriseId) {
        storage.setItem(PORTAL_LAST_ENTID_STORAGE_KEY, `${selectedEnterpriseId}`);
      }
      return {
        ...state,
        selectedEnterpriseId,
      };
    },
  },
  updateAutoPaySetting: {
    action: updateAutoPaySetting,
    reducer: (state, { enterpriseId, enabled }) => {
      const nextEnterpriseList = [...state.enterpriseList];
      const entIndex = nextEnterpriseList.findIndex((value) => value.id === enterpriseId);
      nextEnterpriseList[entIndex] = {
        ...nextEnterpriseList[entIndex],
        autoPay: enabled,
      };

      const nextEnterpriseMap = { ...state.enterpriseMap };
      nextEnterpriseMap[enterpriseId] = {
        ...nextEnterpriseMap[enterpriseId],
        autoPay: enabled,
      };

      return {
        ...state,
        enterpriseList: nextEnterpriseList,
        enterpriseMap: nextEnterpriseMap,
      };
    },
  },
};

export default applyReducers({ store, reducers });
